import { Divider, IconButton, Link, useTheme } from '@material-ui/core';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import { SearchbarX } from 'framework/components/search/SearchbarX';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useLazyEffect } from 'framework/hooks/useLazyEffect';
import {
	CochlearReferralCenterExportProp,
	CochlearReferralCenterExportProps,
	exportCochlearReferralCenters_handle,
	getCochlearReferralCenters_handle,
	ICochlearReferralCenterDto,
	IExportCochlearReferralCentersParams,
} from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useState } from 'react';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { ExportSelectPropsDialog } from 'shared/export/ExportSelectPropsDialog';
import { IReloadTriggerProps } from 'shared/layout/IReloadTriggerProps';

function createExportRecord(strings: IStrings): Record<CochlearReferralCenterExportProp, string> {
	return {
		FormattedAddress: strings.address,
		PostalCode: strings.zip,
		Email: strings.email,
		Name: strings.name,
		PhoneNumber: strings.phone,
	};
}

type CochlearReferralCentersTabProps = IReloadTriggerProps & {
	reload: VoidFunction;
};

export const CochlearReferralCentersTab = ({ reload, reloadTrigger }: CochlearReferralCentersTabProps) => {
	const strings = useLocalization();
	const [data, reloadEvents] = useApiEffect(getCochlearReferralCenters_handle);
	const [search, setSearch] = useState<string>('');
	var fData = (data ?? []).filter(t => t.name?.toLowerCase().includes(search));
	const { open, cancel } = useDialogsContext(reload);

	useLazyEffect(() => {
		reloadEvents();
	}, [reloadTrigger]);

	const onExport = () => {
		open(
			<ExportSelectPropsDialog<IExportCochlearReferralCentersParams, CochlearReferralCenterExportProp>
				open
				fileName={'centra.xlsx'}
				close={cancel}
				params={{ fileName: 'centra.xlsx', format: 'Xlsx', headers: [], mustApplyFilter: true }}
				downloadF={exportCochlearReferralCenters_handle}
				record={createExportRecord(strings)}
				allProps={CochlearReferralCenterExportProps}
				necessaryProps={[]}
				title={`Export centers`}
				format='Xlsx'
				mustApplyFilter={true}
				view='cochlear_referral_centers'
			/>
		);
	};

	if (data === undefined) {
		return <div></div>;
	}

	return (
		<div
			className='df-col gap-8 w100'
			style={{ padding: '16px' }}>
			<div className='df-row-ac gap-8 w100'>
				<SearchbarX
					placeholder={strings.searchVerb}
					value={search}
					onSearch={val => setSearch(val)}
					style={{ width: 500, height: 36 }}
				/>
				<div className='fg1'></div>
				<IconButton
					size='small'
					color='primary'
					onClick={onExport}>
					<GetAppOutlinedIcon />
				</IconButton>
				<IconButton
					size='small'
					color='primary'
					onClick={onExport}>
					<PrintOutlinedIcon />
				</IconButton>
				{/* <IconButton
					size='small'
					color='primary'
					onClick={() => {}}>
					<MailOutlineIcon />
				</IconButton> */}
			</div>
			<Divider />
			{fData.length === 0 && <em>{strings.noResults}</em>}
			<div
				className='df-row-wrap'
				style={{ rowGap: 12, columnGap: 12 }}>
				{fData.map(t => (
					<ReferralCenterCard
						item={t}
						key={t.id}
					/>
				))}
			</div>
		</div>
	);
};

type ReferralCenterCardProps = {
	item: ICochlearReferralCenterDto;
};

const ReferralCenterCard = ({ item }: ReferralCenterCardProps) => {
	const theme = useTheme();

	return (
		<div
			className='df-col gap-4'
			style={{
				border: '1px solid',
				borderRadius: 4,
				borderColor: theme.palette.grey[200],
				padding: 12,
				flexBasis: `calc((100% - 24px) / 3)`,
				maxWidth: `calc((100% - 24px) / 3)`,
				backgroundColor: theme.palette.background.paper,
			}}>
			<strong>{item.name}</strong>
			<div className='df-row-ac gap-4'>
				<LocationOnOutlinedIcon
					fontSize='small'
					color='action'
				/>
				<div style={{ color: theme.palette.text.secondary }}>{item.address.formatted}</div>
			</div>
			{item.phoneNumber && (
				<div className='df-row-ac gap-4'>
					<PhoneOutlinedIcon
						fontSize='small'
						color='action'
					/>
					<div style={{ color: theme.palette.text.secondary }}>{item.phoneNumber}</div>
				</div>
			)}
			{item.email && (
				<div className='df-row-ac gap-4'>
					<MailOutlineIcon
						fontSize='small'
						color='primary'
					/>
					<Link href={`mailto:${item.email}`}>{item.email}</Link>
				</div>
			)}
		</div>
	);
};
